import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckIcon, ClockIcon, ShieldCheckIcon } from "@/components/icons";
import { useDocumentMeta } from "@/src/use-document-meta";

type SubmitState =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "success"; reference: string }
  | { status: "error"; message: string };

export function ContactPage() {
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });

  useDocumentMeta({
    title: "Contact Chuno",
    description: "Contact the Chuno support team about your account, job listings, applications, privacy, or general questions.",
    canonical: "https://www.chuno.work/contact",
  });

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    setSubmitState({ status: "sending" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.get("name"),
          email: values.get("email"),
          topic: values.get("topic"),
          subject: values.get("subject"),
          message: values.get("message"),
          website: values.get("website"),
        }),
      });
      const result = await response.json<{ error?: string; reference?: string }>();
      if (!response.ok) throw new Error(result.error || "We could not send your message. Please try again.");
      form.reset();
      setSubmitState({ status: "success", reference: result.reference || "Received" });
    } catch (error) {
      setSubmitState({ status: "error", message: error instanceof Error ? error.message : "We could not send your message. Please try again." });
    }
  }

  return (
    <main id="main" className="contact-page">
      <section className="contact-hero">
        <div className="shell contact-hero__inner">
          <div>
            <p className="eyebrow"><span /> Contact Chuno</p>
            <h1>Let’s sort it out.</h1>
          </div>
          <p>Questions about your account, a job listing, or how Chuno works? Send us the details and our support team will take a look.</p>
        </div>
      </section>

      <section className="contact-section">
        <div className="shell contact-layout">
          <aside className="contact-details">
            <p className="section-kicker">Support</p>
            <h2>A human way to get help.</h2>
            <p>Include the job URL or account email when it helps us understand the issue. Never send passwords or sensitive identity documents.</p>
            <a className="contact-email" href="mailto:support@chuno.work">support@chuno.work <ArrowUpRight /></a>
            <div className="contact-promises">
              <p><ClockIcon /><span><strong>Thoughtful replies</strong>We’ll respond by email as soon as we can.</span></p>
              <p><ShieldCheckIcon /><span><strong>Private by default</strong>Your message is used only to handle your request.</span></p>
            </div>
          </aside>

          <div className="contact-form-card">
            {submitState.status === "success" ? (
              <div className="contact-success" role="status">
                <span><CheckIcon /></span>
                <p className="section-kicker">Message received</p>
                <h2>Thanks for reaching out.</h2>
                <p>We’ve received your message and will reply to the email address you provided.</p>
                <p className="contact-reference">Reference <strong>{submitState.reference}</strong></p>
                <button className="button button--dark" type="button" onClick={() => setSubmitState({ status: "idle" })}>Send another message</button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={submit}>
                <div className="contact-form__heading">
                  <p className="section-kicker">Send a message</p>
                  <h2>How can we help?</h2>
                </div>

                <div className="contact-form__row">
                  <label><span>Name</span><input name="name" type="text" autoComplete="name" maxLength={80} required /></label>
                  <label><span>Email address</span><input name="email" type="email" autoComplete="email" maxLength={254} required /></label>
                </div>

                <label>
                  <span>What is this about?</span>
                  <select name="topic" defaultValue="general" required>
                    <option value="general">General question</option>
                    <option value="account">Account or sign-in</option>
                    <option value="application">Apply activity</option>
                    <option value="listing">Job listing</option>
                    <option value="privacy">Privacy request</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </label>

                <label><span>Subject</span><input name="subject" type="text" maxLength={160} required /></label>
                <label><span>Message</span><textarea name="message" rows={7} minLength={20} maxLength={5000} required /></label>
                <label className="contact-honeypot" aria-hidden="true"><span>Website</span><input name="website" type="text" tabIndex={-1} autoComplete="off" /></label>

                {submitState.status === "error" ? <p className="contact-form__error" role="alert">{submitState.message}</p> : null}
                <div className="contact-form__footer">
                  <p>By sending this form, you agree that Chuno may use the information to respond to your request. See our <Link to="/privacy">Privacy Policy</Link>.</p>
                  <button className="button button--dark" type="submit" disabled={submitState.status === "sending"}>
                    {submitState.status === "sending" ? "Sending…" : "Send message"} <ArrowUpRight />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
